import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useUpdateAtom } from 'jotai/utils';
import cn from 'classnames';
import { stripQueryCriteriaWildcards } from 'src/shared/chat';
import { applyAiSearchIntentAtom } from 'src/shared/dashboardFilters';
import { useAlerts } from 'src/common/AlertManager';
import { useUIActionDispatcher } from './useUIActionDispatcher';
import { useRollbackRunner, buildUnremovableNote } from './useRollbackRunner';
import { getToolLabel, getBatchPastLabel, getInputSummary } from './actionLabels';
import type { ActionCardData, RollbackState } from './types';
import styles from './BatchActionCard.module.scss';

interface BatchActionCardProps {
  // Group of >= 2 consecutive ActionCardData entries with the same `tool`.
  // Single-entry groups are rendered as a regular `ActionCard` upstream.
  group: ActionCardData[];
}

// Cap concurrent rollback POSTs in "Undo all" so a 50-item batch doesn't
// open 50 simultaneous fetches against the chat-proxy.
const UNDO_ALL_CONCURRENCY = 5;

// Aggregated rollback state for the batch card itself. Per-child state
// lives in `childStates` keyed by ActionCardData.id.
type BatchUndoState = 'idle' | 'in_flight' | 'reverted' | 'partial';

interface ChildState {
  state: RollbackState;
  errorMessage?: string;
}

export const BatchActionCard: React.FC<BatchActionCardProps> = ({ group }) => {
  const [expanded, setExpanded] = useState(false);
  const [batchUndoState, setBatchUndoState] = useState<BatchUndoState>('idle');
  const [childStates, setChildStates] = useState<Record<string, ChildState>>(() =>
    Object.fromEntries(group.map((c) => [c.id, { state: 'idle' as RollbackState }])),
  );
  const [aggregateNote, setAggregateNote] = useState<string | null>(null);

  const history = useHistory();
  const applyAiSearchIntent = useUpdateAtom(applyAiSearchIntentAtom);
  const { showSuccess, showWarning, showInfo, showAlert } = useAlerts();
  const dispatchUIAction = useUIActionDispatcher();
  const runRollback = useRollbackRunner();

  const tool = group[0].tool;
  const label = getToolLabel(tool);

  const counts = useMemo(() => {
    let pending = 0;
    let success = 0;
    let error = 0;
    let cancelled = 0;
    for (const c of group) {
      if (c.status === 'pending') pending++;
      else if (c.status === 'success') success++;
      else if (c.status === 'error') error++;
      else if (c.status === 'cancelled') cancelled++;
    }
    return { pending, success, error, cancelled, total: group.length };
  }, [group]);

  const undoableChildren = useMemo(
    () => group.filter((c) => c.status === 'success' && c.rollback?.opId),
    [group],
  );

  const allUndoable =
    counts.pending === 0 && counts.success > 0 && undoableChildren.length === counts.success;

  // Per-row counts for the batch header status line. During Undo all and
  // afterwards, prefer reverted/failed counts so users can see progress.
  const revertedCount = useMemo(
    () => Object.values(childStates).filter((s) => s.state === 'reverted').length,
    [childStates],
  );
  const inFlightCount = useMemo(
    () => Object.values(childStates).filter((s) => s.state === 'in_flight').length,
    [childStates],
  );
  const failedCount = useMemo(
    () => Object.values(childStates).filter((s) => s.state === 'failed').length,
    [childStates],
  );

  const summaryLine = useMemo(() => {
    if (counts.pending > 0) {
      return `${counts.success + counts.error} / ${counts.total} completed`;
    }
    if (counts.cancelled > 0) {
      // User stopped mid-batch: show what completed before the stop
      // alongside the stopped count so the row isn't misleading.
      const parts: string[] = [];
      if (counts.success > 0) parts.push(`${counts.success} done`);
      if (counts.error > 0) parts.push(`${counts.error} failed`);
      parts.push(`${counts.cancelled} stopped`);
      return parts.join(' · ');
    }
    if (batchUndoState === 'in_flight') {
      return `Reverting ${revertedCount + failedCount} / ${undoableChildren.length}…`;
    }
    if (batchUndoState === 'reverted') {
      return `Reverted (${revertedCount}/${undoableChildren.length})`;
    }
    if (batchUndoState === 'partial') {
      return `Reverted ${revertedCount} · ${failedCount} failed`;
    }
    if (counts.error > 0 && counts.success > 0) {
      return `${counts.success} succeeded · ${counts.error} failed`;
    }
    if (counts.error === counts.total) {
      return `${counts.error} failed`;
    }
    return getBatchPastLabel(tool, counts.success);
  }, [
    counts,
    tool,
    batchUndoState,
    revertedCount,
    failedCount,
    inFlightCount,
    undoableChildren.length,
  ]);

  const headerStatus: 'pending' | 'success' | 'error' | 'warning' | 'cancelled' = useMemo(() => {
    if (counts.pending > 0 || batchUndoState === 'in_flight') return 'pending';
    if (batchUndoState === 'partial' || (counts.error > 0 && counts.error < counts.total)) {
      return 'warning';
    }
    if (counts.error === counts.total && counts.total > 0) return 'error';
    // All stopped before anything completed → neutral cancelled header.
    // A mix of completed + stopped is a partial outcome → warning.
    if (counts.cancelled === counts.total && counts.total > 0) return 'cancelled';
    if (counts.cancelled > 0) return 'warning';
    return 'success';
  }, [counts, batchUndoState]);

  const setChild = useCallback((id: string, patch: Partial<ChildState>) => {
    setChildStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  // Process a single child rollback and update its row state. Returns the
  // outcome so the bulk loop can roll up aggregate counters / unremovable
  // keys / partial-failure asset counts.
  const processOne = useCallback(
    async (child: ActionCardData) => {
      if (!child.rollback) return null;
      setChild(child.id, { state: 'in_flight' });
      const outcome = await runRollback(child.rollback.opId);

      switch (outcome.kind) {
        case 'reverted':
        case 'expired':
        case 'forbidden':
          setChild(child.id, { state: 'reverted' });
          break;
        case 'auth':
        case 'failed':
          setChild(child.id, { state: 'failed', errorMessage: outcome.message });
          break;
      }
      return outcome;
    },
    [runRollback, setChild],
  );

  const handleUndoAll = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!allUndoable || batchUndoState !== 'idle') return;

      setBatchUndoState('in_flight');
      // Mark every eligible child in_flight up front so the per-row UI
      // reflects intent immediately rather than 5-at-a-time.
      setChildStates((prev) => {
        const next = { ...prev };
        for (const c of undoableChildren) {
          next[c.id] = { state: 'in_flight' };
        }
        return next;
      });

      // Concurrency-capped fan-out. Promise.allSettled within each chunk so a
      // single 5xx doesn't abort the rest.
      const results: Array<Awaited<ReturnType<typeof processOne>>> = [];
      for (let i = 0; i < undoableChildren.length; i += UNDO_ALL_CONCURRENCY) {
        const chunk = undoableChildren.slice(i, i + UNDO_ALL_CONCURRENCY);
        const settled = await Promise.allSettled(chunk.map((c) => processOne(c)));
        for (const s of settled) {
          if (s.status === 'fulfilled') results.push(s.value);
          else results.push(null);
        }
      }

      // Roll up the structured outcomes.
      let assetSuccess = 0;
      let assetFailure = 0;
      let assetTotal = 0;
      let opReverted = 0;
      let opFailed = 0;
      const unremovableKeys = new Set<string>();
      for (const outcome of results) {
        if (!outcome) {
          opFailed++;
          continue;
        }
        switch (outcome.kind) {
          case 'reverted':
            opReverted++;
            assetSuccess += outcome.successCount;
            assetFailure += outcome.partialFailureCount;
            assetTotal += outcome.total;
            outcome.unremovableKeys.forEach((k) => unremovableKeys.add(k));
            break;
          case 'expired':
          case 'forbidden':
            // Treated as "nothing to do" — the row is marked reverted but the
            // operation didn't actually move data this time. Don't roll into
            // the aggregate count.
            break;
          case 'auth':
            opFailed++;
            // Auth failure is a session-wide problem; surface it once.
            showAlert(outcome.message);
            break;
          case 'failed':
            opFailed++;
            break;
        }
      }

      const finalNote = buildUnremovableNote(Array.from(unremovableKeys));
      if (finalNote) setAggregateNote(finalNote);

      // One aggregate toast at the end. The dedupe layer in
      // useUIActionDispatcher will not collapse this with anything because
      // its message is unique to this batch.
      if (opFailed === 0 && assetFailure === 0) {
        const msg = assetTotal > 0
          ? `Reverted ${opReverted} metadata change${opReverted === 1 ? '' : 's'} (${assetTotal} asset${assetTotal === 1 ? '' : 's'})`
          : `Reverted ${opReverted} metadata change${opReverted === 1 ? '' : 's'}`;
        showSuccess(msg);
        setBatchUndoState('reverted');
      } else if (opReverted > 0) {
        const totalAttempted = opReverted + opFailed;
        showWarning(
          `Reverted ${opReverted} of ${totalAttempted} — ${opFailed} failed${assetFailure > 0 ? ` (${assetFailure} per-asset)` : ''}`,
        );
        setBatchUndoState('partial');
      } else {
        showAlert(`Couldn't undo ${opFailed} metadata change${opFailed === 1 ? '' : 's'}. Please try again.`);
        // Drop back to idle so the user can retry.
        setBatchUndoState('idle');
      }
    },
    [
      allUndoable,
      batchUndoState,
      undoableChildren,
      processOne,
      showSuccess,
      showWarning,
      showAlert,
    ],
  );

  // Per-row Undo (visible in expanded view). Mirrors the per-op UX but
  // updates per-child state owned by this card.
  const handleUndoOne = useCallback(
    async (e: React.MouseEvent, child: ActionCardData) => {
      e.preventDefault();
      e.stopPropagation();
      if (!child.rollback) return;
      const current = childStates[child.id]?.state || 'idle';
      if (current !== 'idle' && current !== 'failed') return;

      const outcome = await processOne(child);
      if (!outcome) return;

      switch (outcome.kind) {
        case 'reverted': {
          if (outcome.partialFailureCount > 0) {
            showWarning(
              `Reverted ${outcome.successCount} of ${outcome.total} assets — ${outcome.partialFailureCount} failed`,
            );
          } else if (outcome.uiAction?.toast) {
            // Honour the server-provided toast (success).
            dispatchUIAction(outcome.uiAction);
          }
          break;
        }
        case 'expired':
          if (outcome.reason === 'op_already_consumed') showInfo(outcome.message);
          else showWarning(outcome.message);
          break;
        case 'auth':
        case 'forbidden':
        case 'failed':
          showAlert(outcome.message);
          break;
      }
    },
    [childStates, processOne, dispatchUIAction, showAlert, showInfo, showWarning],
  );

  const handleViewClick = useCallback(
    (e: React.MouseEvent, child: ActionCardData) => {
      e.preventDefault();
      e.stopPropagation();
      const link = child.uiAction?.route || child.uiAction?.toast?.link;
      if (child.queryCriteria && child.tool === 'search_assets') {
        const qc = stripQueryCriteriaWildcards(child.queryCriteria);
        applyAiSearchIntent({
          keyword: qc.query || qc.keyword,
          content_type: qc.content_type,
          file_name: qc.file_name,
          status: qc.status,
          upload_time_range: qc.upload_time_range,
          sort_by: qc.sort_by,
          schema_criteria: qc.schema_criteria,
        });
      }
      if (link) history.push(link);
    },
    [history, applyAiSearchIntent],
  );

  const showUndoAllButton =
    allUndoable && (batchUndoState === 'idle' || batchUndoState === 'in_flight');
  const showRevertedPill = batchUndoState === 'reverted';

  return (
    <div
      className={cn(styles.card, {
        [styles.pending]: headerStatus === 'pending',
        [styles.success]: headerStatus === 'success',
        [styles.error]: headerStatus === 'error',
        [styles.warning]: headerStatus === 'warning',
        [styles.cancelled]: headerStatus === 'cancelled',
      })}
    >
      <div className={styles.cardHeader} onClick={() => setExpanded(!expanded)}>
        <div className={styles.statusIcon}>
          {headerStatus === 'pending' && <div className={styles.spinner} />}
          {headerStatus === 'success' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17l-5-5"
                stroke="var(--color-green-400)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {headerStatus === 'warning' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4M12 17h.01"
                stroke="#d97706"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke="#d97706"
                strokeWidth="1.5"
              />
            </svg>
          )}
          {headerStatus === 'error' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4M12 17h.01"
                stroke="var(--color-red-400)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                stroke="var(--color-red-400)"
                strokeWidth="1.5"
              />
            </svg>
          )}
          {headerStatus === 'cancelled' && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect x="6" y="6" width="12" height="12" rx="2" fill="var(--color-gray-400)" />
            </svg>
          )}
        </div>
        <div className={styles.cardInfo}>
          <div className={styles.toolName}>
            {label} <span aria-hidden="true">·</span> {counts.total}
          </div>
          <div className={styles.summary}>{summaryLine}</div>
        </div>
        <div className={styles.cardActions}>
          {showUndoAllButton && (
            <button
              type="button"
              className={styles.undoAllButton}
              onClick={handleUndoAll}
              disabled={batchUndoState !== 'idle'}
              aria-label="Undo all metadata changes in this batch"
              title="Undo all metadata changes in this batch"
            >
              {batchUndoState === 'in_flight' ? (
                <span className={styles.spinnerSm} />
              ) : (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 14L4 9l5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 9h11a5 5 0 0 1 0 10h-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {batchUndoState === 'in_flight' ? 'Reverting…' : 'Undo all'}
            </button>
          )}
          {showRevertedPill && (
            <span className={cn(styles.undoAllButton, styles.undoAllReverted)}>Reverted</span>
          )}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            className={cn(styles.chevron, { [styles.chevronOpen]: expanded })}
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {aggregateNote && <div className={styles.rollbackNote}>{aggregateNote}</div>}

      {expanded && (
        <div className={styles.cardBody}>
          {group.map((child) => {
            const cs = childStates[child.id] || { state: 'idle' as RollbackState };
            const childLink = child.uiAction?.route || child.uiAction?.toast?.link;
            const inputSummary = getInputSummary(child.tool, child.input);
            const childRollbackable = child.status === 'success' && !!child.rollback;
            const childUndoState = cs.state;

            return (
              <div key={child.id} className={styles.childRow}>
                <div className={styles.childStatusIcon}>
                  {childUndoState === 'in_flight' ? (
                    <span className={styles.spinnerSm} />
                  ) : childUndoState === 'reverted' ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="var(--color-gray-400)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : child.status === 'pending' ? (
                    <span className={styles.spinnerSm} />
                  ) : child.status === 'error' ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 9v4M12 17h.01"
                        stroke="var(--color-red-400)"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : child.status === 'cancelled' ? (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect x="6" y="6" width="12" height="12" rx="3" fill="var(--color-gray-400)" />
                    </svg>
                  ) : (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="var(--color-green-400)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className={styles.childLabel}>
                  {inputSummary || child.tool}
                  {cs.state === 'failed' && cs.errorMessage && (
                    <span className={styles.childError}> — {cs.errorMessage}</span>
                  )}
                </div>
                <div className={styles.childActions}>
                  {childLink && (
                    <button
                      type="button"
                      className={styles.viewLink}
                      onClick={(e) => handleViewClick(e, child)}
                    >
                      View
                    </button>
                  )}
                  {childRollbackable && (
                    <button
                      type="button"
                      className={cn(styles.undoButton, {
                        [styles.undoButtonReverted]: childUndoState === 'reverted',
                      })}
                      onClick={(e) => handleUndoOne(e, child)}
                      disabled={
                        childUndoState === 'in_flight' ||
                        childUndoState === 'reverted' ||
                        batchUndoState === 'in_flight'
                      }
                      aria-label={childUndoState === 'reverted' ? 'Reverted' : 'Undo this change'}
                      title={childUndoState === 'reverted' ? 'Reverted' : 'Undo this change'}
                    >
                      {childUndoState === 'reverted' ? 'Reverted' : 'Undo'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
